import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import _ from "lodash/fp";
import {
  CloseCircle as CloseCircleIcon,
  Delete as DeleteIcon,
  DotsVertical as DotsVerticalIcon,
  Pencil as PencilIcon,
  TextBoxSearch as TextBoxSearchIcon,
  ViewSplitHorizontal as ViewSplitHorizontalIcon,
  ViewSplitVertical as ViewSplitVerticalIcon,
} from "@actinc/dls/icons/";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import {
  AppBar,
  Box,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Tab,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";

import {
  CompareReferencePassageDialog,
  ControlledFormEditor,
  SRAccessibilityPanel,
  TTSAccessibilityPanel,
} from "../../../components/";
import {
  TTSStatusIcon,
  selectTTSIconType,
} from "../../../components/TTSStatusIcon";
import {
  deleteDynamicTags,
  deleteHighlights,
  getCleanContent,
  getCorrectDynamicTagPosition,
  getCurrentDynamicTagPosition,
  getDynamicContentElement,
  getDynamicModifyContentTagRange,
  getDynamicTagOptions,
  getOrderedElements,
  getSentenceRanges,
  insertDynamicTagIntoContent,
} from "../../../services/contentUtils";
import {
  useMemoCompare,
  useSessionStore,
  useSize,
  useStore,
} from "../../../services/hooks";
import { getHighlightPosition } from "../../../services/passage";
import { filterPassageTemplatesByContentArea } from "../../../services/templates";
import { MetadataConfigurationContext } from "../../../metadataConfigurationProvider";
import { getTaskPermissions, isAdmin } from "../../../services/utils";

import { UserContext } from "../../../userProvider";

import { DynamicModifyContentTagDialog } from "./DynamicModifyContentTagDialog";
import { DynamicTagListItem } from "./DynamicTagListItem";
import { DynamicOrderContentTagDialog } from "./DynamicOrderContentTagDialog";
import { DynamicRemoveLabelsTagDialog } from "./DynamicRemoveLabelsTagDialog";
import { SelectItemsDialog } from "./SelectItemsDialog";
import {
  READY_COMPONENT_TYPE,
  useReadyContext,
} from "../../../providers/ReadyProvider";
import {
  calculateOverallStatus,
  hasProcessingDbTags,
  hasRetryableDbTags,
  TTS_DEBUG,
  TTSTagStatusDBEnum,
  useTTSFileStatusContext,
} from "../../../providers/TTSFileStatusProvider";

const PassageContentCard = ({ activeTasks, readonly }) => {
  const cardName = `PassageContentCard`;

  const [workspace, workspaceStore] = useSessionStore("workspace");
  const { orientation = "left-right", selectedTab = "Content" } =
    useMemoCompare(workspace?.cards.find((c) => c.i === cardName).state || {});

  const { control, getValues, setValue, watch } = useFormContext();
  const [itemID, formPassageContentArea] = watch([
    "item.id",
    "passage.contentArea",
  ]);

  const {
    fields: dynamicTags,
    insert: insertDynamicTag,
    move: moveDynamicTag,
    replace: replaceDynamicTags,
    update: updateDynamicTag,
  } = useFieldArray({
    control,
    name: `passage.dynamicTags`,
  });
  const {
    fields: highlights,
    insert: insertHighlight,
    replace: replaceHighlights,
    update: updateHighlight,
  } = useFieldArray({
    control,
    name: `passage.highlights`,
  });

  const { markReady } = useReadyContext();

  const dynamicPassageContentRef = useRef();
  const tabHeaderRef = useRef();

  const [cardContentRef, setCardContentRef] = useState();
  const size = useSize(cardContentRef);

  const [bulkDynamicTagActionsAnchorEl, setBulkDynamicTagActionsAnchorEl] =
    useState(null);
  const [
    compareReferencePassageDialogProps,
    setCompareReferencePassageDialogProps,
  ] = useState({ open: false });
  const [contextMenuState, setContextMenuState] = useState({
    mouseX: null,
    mouseY: null,
    options: [],
    selection: null,
  });
  const [dynamicContentElement, setDynamicContentElement] = useState();
  const [dynamicContentLabels, setDynamicContentLabels] = useState();
  const [modifyContentDialogState, setModifyContentDialogState] =
    useState(null);
  const [modifyContentDialogOpen, setModifyContentDialogOpen] = useState(false);
  const [orderContentDialogState, setOrderContentDialogState] = useState(null);
  const [orderContentDialogOpen, setOrderContentDialogOpen] = useState(false);
  const [removeLabelsDialogState, setRemoveLabelsDialogState] = useState(null);
  const [removeLabelsDialogOpen, setRemoveLabelsDialogOpen] = useState(false);
  const [editor, setEditor] = useState();
  const [selectItemsDialogOpen, setSelectItemsDialogOpen] = useState(false);
  const [selectItemsDialogState, setSelectItemsDialogState] = useState(null);

  const [passage] = useStore("passage");

  const metadataConfiguration = useContext(MetadataConfigurationContext);
  const passageTemplates = useMemo(
    () =>
      filterPassageTemplatesByContentArea(metadataConfiguration?.templates)(
        formPassageContentArea,
      ),
    [metadataConfiguration, formPassageContentArea],
  );

  const setupDynamicTab = (content) => {
    const [contentElement, labels] = getDynamicContentElement({
      ...getValues("passage"),
      content,
    })();
    setDynamicContentElement(contentElement);
    setDynamicContentLabels(labels);
  };

  const permissions = useMemo(
    () => getTaskPermissions(activeTasks.passage),
    [activeTasks.passage],
  );

  const user = useContext(UserContext);

  const watchedPassageAccessibilityElements = useWatch({
    control,
    name: "passage.accessibilityElements",
  });
  const currentTTSInObjectDb = useMemo(
    () => watchedPassageAccessibilityElements ?? [],
    [watchedPassageAccessibilityElements],
  );
  // useWatch (reactive) for ttsTagStatuses so the icon re-renders when the
  // refetch effect lands fresh dbStatuses via setValue. getValues alone is
  // non-reactive and would leave retryableDb stale until the next external
  // re-render.
  const watchedPassageTtsTagStatuses = useWatch({
    control,
    name: "passage.ttsTagStatuses",
  });

  const ttsObjectStatus = useMemo(() => {
    const oldTTS = passage?.accessibilityElements ?? [];
    const isUnchanged =
      currentTTSInObjectDb.length === oldTTS.length &&
      currentTTSInObjectDb.every(
        (tag, index) => tag.identifier === oldTTS[index]?.identifier,
      );

    return isUnchanged && oldTTS.length > 0
      ? TTSTagStatusDBEnum.SAVED
      : TTSTagStatusDBEnum.PENDING;
  }, [currentTTSInObjectDb, passage?.accessibilityElements]);

  const { ttsTagStatusMap } = useTTSFileStatusContext();

  const ttsTagStatus = useMemo(() => {
    const ttsFilesMap = ttsTagStatusMap[getValues("passage.id")];
    return calculateOverallStatus(ttsFilesMap, currentTTSInObjectDb.length);
  }, [ttsTagStatusMap, currentTTSInObjectDb.length, getValues]);

  useEffect(() => {
    if (!TTS_DEBUG) return;
    const liveMap = ttsTagStatusMap[getValues("passage.id")] ?? {};
    const liveEntries = Object.entries(liveMap).filter(
      ([k]) => k !== "__requestStatus",
    );
    const inProgress = liveEntries.filter(([, v]) =>
      ["PROCESSING", "ENQUEUED"].includes(v.status),
    ).length;
    const failed = liveEntries.filter(([, v]) => v.status === "FAILED").length;
    const completed = liveEntries.filter(
      ([, v]) => v.status === "GENERATED",
    ).length;
    const total = (passage?.accessibilityElements ?? []).length;
    const requestStatus = liveMap["__requestStatus"];
    // NOTE: inProgress is always 0 — individual tags only receive GENERATED via WebSocket,
    // never PROCESSING/ENQUEUED. PROCESSING state comes from __requestStatus, not tag messages.
    console.info(
      `[TTS Tab - passage] live: ${completed} GENERATED / ${inProgress} PROCESSING / ${failed} FAILED` +
        ` | totalExpected: ${currentTTSInObjectDb.length} (savedTotal: ${total})` +
        ` | overallStatus: ${ttsTagStatus} | __requestStatus: ${requestStatus?.status ?? "none"}`,
    );
  }, [
    ttsTagStatusMap,
    ttsTagStatus,
    ttsObjectStatus,
    getValues,
    passage?.accessibilityElements,
    currentTTSInObjectDb.length,
  ]);

  const ttsIconType = useMemo(() => {
    const dbTagStatuses = watchedPassageTtsTagStatuses ?? [];
    const liveMap = ttsTagStatusMap[getValues("passage.id")] ?? {};
    return selectTTSIconType({
      hasTTSTags:
        currentTTSInObjectDb.length > 0 ||
        (passage?.accessibilityElements ?? []).length > 0,
      ttsTagStatus,
      ttsObjectStatus,
      retryable: hasRetryableDbTags(dbTagStatuses, liveMap),
      processingDb: hasProcessingDbTags(dbTagStatuses, liveMap),
    });
  }, [
    watchedPassageTtsTagStatuses,
    ttsTagStatusMap,
    currentTTSInObjectDb.length,
    passage?.accessibilityElements,
    ttsTagStatus,
    ttsObjectStatus,
    getValues,
  ]);

  const tabs = useMemo(
    () =>
      [
        {
          filter:
            !permissions.includes("accessibility") ||
            isAdmin(user)(passage?.program),
          label: "Edit Content",
          value: "Content",
        },
        {
          filter:
            formPassageContentArea === "English" &&
            ((!activeTasks.passage &&
              passage?._actions?.includes("dynamicPassage")) ||
              activeTasks.passage?._actions.includes("dynamicPassage")),
          label: "Dynamic Passage",
          value: "DynamicPassage",
        },
        {
          filter:
            (!activeTasks.passage &&
              passage?._actions.includes("viewAccessibility")) ||
            permissions.includes("accessibility"),
          label: (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              TTS
              <TTSStatusIcon type={ttsIconType} debugContext="passage" />
            </Box>
          ),
          value: "TTS",
        },
        {
          filter:
            (!activeTasks.passage &&
              passage?._actions.includes("viewAccessibility")) ||
            permissions.includes("accessibility"),
          label: "SR",
          value: "SR",
        },
      ].filter(({ filter }) => filter),
    [
      activeTasks.passage,
      formPassageContentArea,
      passage?._actions,
      passage?.ttsTagStatuses,
      permissions,
      ttsTagStatus,
      ttsTagStatusMap,
      ttsObjectStatus,
      ttsIconType,
      currentTTSInObjectDb.length,
    ],
  );

  const highlightHighlightInContent = (hl) => {
    const command = editor?.commands.get("displayItemHighlights");
    command?.isEnabled &&
      command.execute(
        [...highlights].map((highlight) => {
          const title = passage.items
            .filter((item) => highlight.items?.includes(item.id))
            .map((item) => item.name)
            .join(", ");
          return { ...highlight, title };
        }),
      )(hl);
  };

  useEffect(() => {
    if (editor) {
      editor.fire("cde:setItem", itemID || null);
      highlightHighlightInContent();
    }
  }, [editor, highlights, itemID]);
  useEffect(() => {
    if (selectedTab !== "DynamicPassage") return;
    setupDynamicTab(getValues("passage.content"));
  }, [selectedTab]);
  useEffect(() => {
    if (selectedTab !== "DynamicPassage") return;
    setupDynamicTab(
      getCleanContent(dynamicPassageContentRef.current) ||
        getValues("passage.content"),
    );
  }, [dynamicTags, getValues, highlights, itemID]);
  useEffect(() => {
    if (!tabs.some((tab) => tab.value === selectedTab))
      workspaceStore.selectTab(cardName)(null, tabs.at(0).value);
  }, [tabs]);

  const closeBulkDynamicTagActionsMenu = () => {
    setBulkDynamicTagActionsAnchorEl(null);
  };
  const closeContextMenu = () => {
    setContextMenuState({
      mouseX: null,
      mouseY: null,
      options: [],
      selection: null,
    });
  };

  const createHighlight = (_eventInfo, ...args) => {
    const { data: content, ...highlight } = args[0];
    if (!highlight) return;

    // NOTE: Need to use getValues() here because the highlights from useFieldArray isn't updating in this function
    const position = getHighlightPosition(content)(
      getValues("passage.highlights"),
    )(highlight);
    insertHighlight(position, highlight);
  };
  const deleteAllDynamicTags = () => {
    const [content, tags] = deleteDynamicTags(getValues("passage"))(
      dynamicPassageContentRef.current,
    )([...getValues(`passage.dynamicTags`)]);
    replaceDynamicTags(tags.dynamicTags);
    replaceHighlights(tags.highlights);
    setValue("passage.content", content, {
      shouldDirty: true,
    });
    closeBulkDynamicTagActionsMenu();
  };

  const deleteDynamicTag = (index) => {
    const [content, tags] = deleteDynamicTags(getValues("passage"))(
      dynamicPassageContentRef.current,
    )([{ ...getValues(`passage.dynamicTags[${index}]`) }]);
    replaceDynamicTags(tags.dynamicTags);
    replaceHighlights(tags.highlights);
    setValue("passage.content", content, {
      shouldDirty: true,
    });
  };

  const deleteItemFromHighlight = (index) => (id) => () => {
    const highlight = highlights[index];
    if (highlight.items.length > 1) {
      const items = highlight.items.filter((item) => item !== id);
      updateHighlight(index, {
        ...highlight,
        items,
      });
    } else {
      const contentElement = document.createElement("div");
      contentElement.innerHTML = getValues("passage.content");
      const [content, tags] = deleteHighlights(getValues("passage"))(
        contentElement,
      )([{ ...getValues(`passage.highlights[${index}]`) }]);
      replaceDynamicTags(tags.dynamicTags);
      replaceHighlights(tags.highlights);
      editor.data.set(content);
    }
  };

  const getTabHeight = () => {
    const cardHeight = size?.height || 0;
    const tabHeaderHeight = tabHeaderRef.current
      ? tabHeaderRef.current.getBoundingClientRect().height
      : 0;
    return `${cardHeight - tabHeaderHeight}px`;
  };
  const handleMouseUp = (e) => {
    if (e.button !== 0) return;
    const selection = window.getSelection();
    let range;

    // Handle the case where the user is clicking inside an IPR, but window.getSelection() is targeting the left-adjacent text node
    if (
      selection.isCollapsed &&
      /^mark$/i.test(e.target.tagName) &&
      e.target.textContent === "*"
    ) {
      range = new Range();
      range.setStart(e.target.childNodes[0], 0);
      range.setEnd(e.target.childNodes[0], 0);
    } else {
      range = selection.getRangeAt(0).cloneRange();
    }
    setContextMenuState({
      ...contextMenuState,
      mouseX: e.clientX,
      mouseY: e.clientY,
      options: getDynamicTagOptions(dynamicPassageContentRef.current)(range)({
        highlights,
        itemID,
        items: passage.items,
        labels: dynamicContentLabels,
      }),
    });
  };

  const handleUpdateDynamicTag =
    (create) =>
    ({ filter, label, ...tag }) => {
      if (!tag.items.length) return;

      const [newContentElement, newDynamicTag] = insertDynamicTagIntoContent(
        dynamicPassageContentRef.current,
      )(tag);

      const correctPosition = getCorrectDynamicTagPosition(
        newContentElement.innerHTML,
      )(dynamicTags)(newDynamicTag);
      const currentPosition =
        getCurrentDynamicTagPosition(dynamicTags)(newDynamicTag);
      if (create) {
        insertDynamicTag(correctPosition, newDynamicTag);
      } else {
        updateDynamicTag(currentPosition, newDynamicTag);
        if (correctPosition !== currentPosition) {
          moveDynamicTag(currentPosition, correctPosition);
        }
      }

      setValue("passage.content", getCleanContent(newContentElement), {
        shouldDirty: true,
      });
    };
  const handleUpdateSelectedItems = (create) => (obj) => {
    if (obj.__typename === "DynamicBreakParagraphTag") {
      handleUpdateDynamicTag(create)(obj);
    }
    if (
      obj.__typename === "Highlight" ||
      obj.__typename === "ItemPointReference"
    ) {
      updateHighlight(selectItemsDialogState.index, obj);
    }
    setSelectItemsDialogState(null);
  };
  const highlightDynamicTagInContent = (ref) => (tag) => {
    const [contentElement] = getDynamicContentElement(getValues("passage"))(
      tag,
    );
    ref.innerHTML = contentElement.innerHTML;
  };

  const onReady = (ed) => {
    console.info("Passage Content editor is ready");
    setEditor(ed);
    ed.on("cde:createHighlight", createHighlight);
    markReady([passage], READY_COMPONENT_TYPE.PASSAGE);
  };
  const openBulkDynamicTagActionsMenu = (e) => {
    setBulkDynamicTagActionsAnchorEl(e.target);
  };
  const openCompareReferencePassageDialog = (_e) => {
    closeBulkDynamicTagActionsMenu();
    setCompareReferencePassageDialogProps({
      closeOptions: ["close"],
      onClose: () => setCompareReferencePassageDialogProps({ open: false }),
      open: true,
      passageID: passage.id,
    });
  };
  const openDynamicTagDialog = (create) => (tag) => () => {
    if (!tag) return;

    // eslint-disable-next-line default-case
    switch (tag.__typename) {
      case "DynamicBreakParagraphTag":
        setSelectItemsDialogState({
          create,
          obj: tag,
        });
        setSelectItemsDialogOpen(true);
        break;
      case "DynamicModifyContentTag":
        const range = getDynamicModifyContentTagRange(dynamicContentElement)(
          tag,
        );
        setModifyContentDialogState({
          create,
          tag: { range, ...tag },
        });
        setModifyContentDialogOpen(true);
        break;
      case "DynamicOrderContentTag":
        const paragraphs = getOrderedElements(dynamicPassageContentRef.current)(
          "p[data-uuid]",
        ).map((element) => ({
          element,
          id: element.getAttribute("data-uuid"),
        }));
        const sentences = getSentenceRanges(dynamicPassageContentRef.current)(
          dynamicContentLabels,
        );
        let blocks, type;
        if (
          tag.label === "Reorder Paragraphs" ||
          (paragraphs.length &&
            paragraphs.every(({ id }) => tag.order.includes(id)))
        ) {
          blocks = paragraphs;
          type = "paragraph";
        }
        if (
          tag.label === "Reorder Sentences" ||
          (sentences.length &&
            sentences.every(({ id }) => tag.order.includes(id)))
        ) {
          blocks = sentences;
          type = "sentence";
        }
        if (!type) {
          console.warn(
            "Not all paragraphs/sentences represented in Order Content tag; delete it and re-apply",
            tag,
            paragraphs,
            sentences,
          );
          return;
        }
        setOrderContentDialogState({ blocks, create, tag, type });
        setOrderContentDialogOpen(true);
        break;
      case "DynamicRemoveLabelsTag":
        setRemoveLabelsDialogState({
          create,
          ranges: dynamicContentLabels,
          tag,
        });
        setRemoveLabelsDialogOpen(true);
        break;
    }
    closeContextMenu();
  };
  const openSelectItemsDialog = (__typename) => (index) => () => {
    let obj;
    // eslint-disable-next-line default-case
    switch (__typename) {
      case "DynamicBreakParagraphTag":
        obj = dynamicTags[index];
        break;
      case "Highlight":
      case "ItemPointReference":
        obj = highlights[index];
        break;
    }
    if (!obj) return;

    setSelectItemsDialogState({ create: false, index, obj });
    setSelectItemsDialogOpen(true);
  };
  const resetDynamicContent = (ref) => (content) => {
    ref.innerHTML = content;
  };

  if (!passage || !getValues("passage.id")) return null;

  return (
    <>
      <Box
        ref={setCardContentRef}
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          "& .qti-visually-hidden": {
            display: "none",
          },
        }}
      >
        <TabContext value={selectedTab}>
          <AppBar
            color="transparent"
            elevation={1}
            position="static"
            ref={tabHeaderRef}
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
            }}
          >
            <TabList
              variant="scrollable"
              onChange={workspaceStore.selectTab(cardName)}
            >
              {tabs.map((tab) => (
                <Tab key={tab.value} label={tab.label} value={tab.value} />
              ))}
            </TabList>
            <ToggleButtonGroup
              exclusive
              onChange={workspaceStore.setOrientation(cardName)}
              size="small"
              value={orientation}
            >
              <ToggleButton value="left-right">
                <ViewSplitVerticalIcon />
              </ToggleButton>
              <ToggleButton value="top-bottom">
                <ViewSplitHorizontalIcon />
              </ToggleButton>
            </ToggleButtonGroup>
          </AppBar>
          <TabPanel
            sx={{ display: "flex", overflowY: "auto", padding: 0 }}
            value="Content"
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: orientation === "left-right" ? "row" : "column",
                flexGrow: 1,
                height: getTabHeight,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  flexGrow: 1,
                  height: "100%",
                  overflowX: "hidden",
                  overflowY: "auto",
                  width: "100%",
                  '& .ck.ck-editor[role="application"]': {
                    display: "flex",
                    flexDirection: "column",
                    flexGrow: 1,
                    '& .ck.ck-editor__main[role="presentation"]': {
                      display: "flex",
                      flexDirection: "column",
                      flexGrow: 1,
                      height: 100,
                      '& .ck.ck-content.ck-editor__editable[role="textbox"]': {
                        flexGrow: 1,
                        "& mark.highlight": {
                          color: "inherit",
                          fontWeight: "bold",
                        },
                        "& mark.highlight::before": {
                          content: '"["',
                        },
                        "& mark.highlight::after": {
                          content: '"]"',
                        },
                        "& .qti-visually-hidden": {
                          display: "none",
                        },
                        "& figure.table": {
                          marginLeft: "0px",
                        },
                        "& thead tr th": {
                          textAlign: "center",
                          verticalAlign: "bottom",
                        },
                      },
                    },
                  },
                }}
              >
                <ControlledFormEditor
                  _actions={passage?._actions}
                  assets={{
                    images: passage.imageAssets,
                    medias: passage.mediaAssets,
                  }}
                  comments={"passage.contentComments"}
                  contentArea={formPassageContentArea}
                  control={control}
                  id="passage.content"
                  name="passage.content"
                  onReady={onReady}
                  options={[
                    "applyTemplate",
                    "createHighlight",
                    "content",
                    permissions.includes("suggestChanges") && "suggestChanges",
                    permissions.includes("acceptChanges") && "acceptChanges",
                  ].filter(Boolean)}
                  readonly={readonly}
                  suggestions={"passage.contentSuggestions"}
                  templates={passageTemplates}
                  type="passage"
                />
              </Box>
              <Box
                sx={{
                  display: highlights.length ? "flex" : "none",
                  flexGrow: 1,
                  maxHeight: orientation === "left-right" ? "100%" : 200,
                  maxWidth: orientation === "left-right" ? 300 : "100%",
                  minHeight: 200,
                  minWidth: 300,
                  overflowY: "auto",
                }}
              >
                <List
                  dense={true}
                  sx={{
                    backgroundColor: "background.paper",
                    flexDirection: "column",
                    flexGrow: 1,
                    paddingTop: 0,
                    position: "relative",
                  }}
                >
                  {highlights?.map((hl, index) => {
                    const hls = highlights.filter(
                      (hl) => hl?.__typename === "Highlight",
                    );
                    const ips = highlights.filter(
                      (hl) => hl?.__typename === "ItemPointReference",
                    );
                    return (
                      <ListItem
                        key={hl.id}
                        onMouseEnter={() => highlightHighlightInContent(hl)}
                        onMouseLeave={() => highlightHighlightInContent()}
                        sx={{ borderTop: "1px solid lightgrey" }}
                      >
                        <ListItemText
                          disableTypography={true}
                          secondary={
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "row",
                                flexWrap: "wrap",
                              }}
                            >
                              {passage.items
                                .filter((item) => hl.items?.includes(item.id))
                                .map((item) => {
                                  const color =
                                    item.id === itemID ? "primary" : "default";
                                  return readonly ? (
                                    <Chip
                                      color={color}
                                      key={item.id}
                                      label={item.name}
                                      size="small"
                                    />
                                  ) : (
                                    <Chip
                                      color={color}
                                      deleteIcon={<CloseCircleIcon />}
                                      key={item.id}
                                      label={item.name}
                                      onDelete={deleteItemFromHighlight(index)(
                                        item.id,
                                      )}
                                      size="small"
                                    />
                                  );
                                })}
                            </Box>
                          }
                        >
                          <Typography variant="subtitle1">
                            {hl?.__typename === "ItemPointReference"
                              ? `Item Point Reference ${
                                  ips.findIndex(
                                    (highlight) => highlight?.id === hl?.id,
                                  ) + 1
                                }`
                              : `Highlight ${
                                  hls.findIndex(
                                    (highlight) => highlight?.id === hl?.id,
                                  ) + 1
                                }`}
                          </Typography>
                        </ListItemText>
                        <ListItemSecondaryAction
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          {!readonly && (
                            <IconButton
                              onClick={openSelectItemsDialog(hl.__typename)(
                                index,
                              )}
                              size="small"
                            >
                              <PencilIcon />
                            </IconButton>
                          )}
                        </ListItemSecondaryAction>
                      </ListItem>
                    );
                  })}
                </List>
              </Box>
            </Box>
          </TabPanel>
          <TabPanel
            sx={{ display: "flex", overflowY: "auto", padding: 0 }}
            value="DynamicPassage"
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: orientation === "left-right" ? "row" : "column",
                flexGrow: 1,
                height: getTabHeight,
              }}
            >
              <Box
                sx={{
                  border: "1px solid lightgrey",
                  display: "flex",
                  flexDirection: "column",
                  flexGrow: 1,
                  height: "100%",
                  overflowY: "auto",
                  paddingX: 1,
                  width: "100%",
                }}
              >
                <Box
                  dangerouslySetInnerHTML={{
                    __html: dynamicContentElement?.innerHTML,
                  }}
                  id="dynamic-passage-content"
                  onMouseUp={(e) => !readonly && handleMouseUp(e)}
                  ref={dynamicPassageContentRef}
                  sx={{
                    ...highlights.reduce((acc, hl) => {
                      if (hl.__typename === "Highlight") return { ...acc };
                      return {
                        ...acc,
                        [`& mark[data-uuid="${hl.point}"]`]: {
                          fontWeight: "bold",
                        },

                        [`& mark[data-uuid="${hl.point}"]::before`]: {
                          content: '"["',
                        },
                        [`& mark[data-uuid="${hl.point}"]::after`]: {
                          content: '"]"',
                        },
                      };
                    }, {}),
                    "& .stimulus_paragraph_number": {
                      display: "flex",
                      justifyContent: "center",
                    },
                    "& .highlight, & .highlight *": {
                      backgroundColor: "lightyellow",
                    },
                    "& .modifyContent, & .modifyContent *": {
                      backgroundColor: "lightskyblue",
                    },
                    "& .underline, & .underline *": {
                      borderBottom: "1px solid black",
                    },
                    "& .removeLabels, & .removeLabels *": {
                      backgroundColor: "#4AE08C",
                    },
                    "& .breakParagraph, & .breakParagraph *": {
                      backgroundColor: "#F1A9A0",
                    },
                    // Must be last to override
                    "& .modifyContent.active, & .modifyContent.active *": {
                      backgroundColor: "#6698FF",
                    },
                    "& .removeLabels.active, & .removeLabels.active *": {
                      backgroundColor: "#26C281",
                    },
                    "& .breakParagraph.active, & .breakParagraph.active *": {
                      backgroundColor: "#FC6399",
                    },
                  }}
                />
                {!!contextMenuState.options.length && (
                  <Menu
                    keepMounted
                    open={contextMenuState.mouseY !== null}
                    onClose={closeContextMenu}
                    anchorReference="anchorPosition"
                    anchorPosition={
                      contextMenuState.mouseX !== null &&
                      contextMenuState.mouseY !== null
                        ? {
                            left: contextMenuState.mouseX,
                            top: contextMenuState.mouseY,
                          }
                        : undefined
                    }
                  >
                    {contextMenuState.options.map((option) => (
                      <MenuItem
                        dense={true}
                        key={option.label}
                        onClick={openDynamicTagDialog(true)(option)}
                      >
                        {option.label}
                      </MenuItem>
                    ))}
                  </Menu>
                )}
              </Box>
              <Box
                sx={{
                  display: dynamicTags.length ? "flex" : "none",
                  flexGrow: 1,
                  height: "100%",
                  maxHeight: orientation === "left-right" ? "100%" : 250,
                  maxWidth: orientation === "left-right" ? 400 : "100%",
                  minHeight: 250,
                  minWidth: 400,
                  overflowY: "auto",
                }}
              >
                <List
                  dense={true}
                  subheader={
                    <ListSubheader
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box>Dynamic Tags</Box>
                      <IconButton
                        aria-label="more"
                        aria-controls={
                          bulkDynamicTagActionsAnchorEl
                            ? "dynamic-tags-bulk-actions-menu"
                            : undefined
                        }
                        aria-expanded={
                          bulkDynamicTagActionsAnchorEl ? "true" : undefined
                        }
                        aria-haspopup="true"
                        disabled={readonly}
                        id="dynamic-actions-bulk-actions"
                        onClick={openBulkDynamicTagActionsMenu}
                      >
                        <DotsVerticalIcon />
                      </IconButton>
                      <Menu
                        anchorEl={bulkDynamicTagActionsAnchorEl}
                        anchorOrigin={{
                          horizontal: "right",
                          vertical: "bottom",
                        }}
                        id="dynamic-tags-bulk-actions-menu"
                        MenuListProps={{
                          "aria-labelledby": "dynamic-tags-bulk-actions",
                        }}
                        onClose={closeBulkDynamicTagActionsMenu}
                        open={Boolean(bulkDynamicTagActionsAnchorEl)}
                        transformOrigin={{
                          horizontal: "right",
                          vertical: "top",
                        }}
                      >
                        <MenuItem onClick={openCompareReferencePassageDialog}>
                          <ListItemIcon>
                            <TextBoxSearchIcon size="small" />
                          </ListItemIcon>
                          <ListItemText>
                            Compare to Reference Passage
                          </ListItemText>
                        </MenuItem>
                        <MenuItem onClick={deleteAllDynamicTags}>
                          <ListItemIcon>
                            <DeleteIcon size="small" />
                          </ListItemIcon>
                          <ListItemText>Delete All</ListItemText>
                        </MenuItem>
                      </Menu>
                    </ListSubheader>
                  }
                  sx={{
                    backgroundColor: "background.paper",
                    flexDirection: "column",
                    flexGrow: 1,
                    overflowX: "hidden",
                    overflowY: "auto",
                    paddingTop: 0,
                    position: "relative",
                  }}
                >
                  {!!dynamicTags.length &&
                    dynamicTags.map((tag, index) => {
                      const orderContentType =
                        tag.__typename === "DynamicOrderContentTag"
                          ? tag.order.some((labelUUID) =>
                              dynamicContentLabels?.find(
                                ({ begin, type }) =>
                                  type === "sentence" && begin === labelUUID,
                              ),
                            )
                            ? "Sentences"
                            : "Paragraphs"
                          : undefined;
                      const hasHover =
                        tag.__typename !== "DynamicOrderContentTag";
                      return (
                        <DynamicTagListItem
                          key={tag.id}
                          tag={tag}
                          index={index}
                          passageItems={passage.items}
                          itemID={itemID}
                          readonly={readonly}
                          dynamicContentLabels={dynamicContentLabels}
                          orderContentType={orderContentType}
                          onEdit={openDynamicTagDialog(false)(tag)}
                          onDelete={deleteDynamicTag}
                          onMouseEnter={
                            hasHover
                              ? () =>
                                  highlightDynamicTagInContent(
                                    dynamicPassageContentRef.current,
                                  )(tag)
                              : undefined
                          }
                          onMouseLeave={
                            hasHover
                              ? () =>
                                  resetDynamicContent(
                                    dynamicPassageContentRef.current,
                                  )(dynamicContentElement.innerHTML)
                              : undefined
                          }
                        />
                      );
                    })}
                </List>
              </Box>
            </Box>
          </TabPanel>
          <TabPanel
            sx={{ display: "flex", overflowY: "auto", padding: 0 }}
            value="TTS"
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: orientation === "left-right" ? "row" : "column",
                flexGrow: 1,
                height: getTabHeight,
              }}
            >
              <TTSAccessibilityPanel
                objectType="passage"
                orientation={orientation}
                readonly={
                  !passage?._actions.includes("manageAccessibility") &&
                  !permissions.includes("accessibility")
                }
              />
            </Box>
          </TabPanel>
          <TabPanel
            sx={{ display: "flex", overflowY: "auto", padding: 0 }}
            value="SR"
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: orientation === "left-right" ? "row" : "column",
                flexGrow: 1,
                height: getTabHeight,
              }}
            >
              <SRAccessibilityPanel
                objectType="passage"
                orientation={orientation}
                readonly={
                  !passage?._actions.includes("manageAccessibility") &&
                  !permissions.includes("accessibility")
                }
              />
            </Box>
          </TabPanel>
        </TabContext>
      </Box>
      {compareReferencePassageDialogProps.open && (
        <CompareReferencePassageDialog
          {...compareReferencePassageDialogProps}
        />
      )}
      {modifyContentDialogOpen && (
        <DynamicModifyContentTagDialog
          {...modifyContentDialogState}
          items={passage.items}
          onClose={handleUpdateDynamicTag}
          openState={[modifyContentDialogOpen, setModifyContentDialogOpen]}
          readonly={readonly}
        />
      )}
      {orderContentDialogOpen && (
        <DynamicOrderContentTagDialog
          {...orderContentDialogState}
          items={passage.items}
          onClose={handleUpdateDynamicTag}
          openState={[orderContentDialogOpen, setOrderContentDialogOpen]}
          readonly={readonly}
        />
      )}
      {removeLabelsDialogOpen && (
        <DynamicRemoveLabelsTagDialog
          {...removeLabelsDialogState}
          items={passage.items}
          onClose={handleUpdateDynamicTag}
          openState={[removeLabelsDialogOpen, setRemoveLabelsDialogOpen]}
          readonly={readonly}
        />
      )}
      {selectItemsDialogOpen && (
        <SelectItemsDialog
          {...selectItemsDialogState}
          items={passage.items}
          onClose={handleUpdateSelectedItems}
          openState={[selectItemsDialogOpen, setSelectItemsDialogOpen]}
          readonly={readonly}
        />
      )}
    </>
  );
};

export { PassageContentCard };
